/**
 * @file Bantoa Virtual Try-On — explicit JS integration via `bnt_r_ai('render', 'virtual-try-on', ...)`.
 * @see https://www.bantoa.com/docs/tech/it/vto.html
 */

/**
 * @typedef {Object} BantoaVtoRawConfig
 * @property {string|number} items - Variant ID for the try-on product.
 * @property {string} position - Unique tracking position identifier.
 * @property {string} buttonText - Button label used for tracking callbacks.
 * @property {string} shareText - Pre-filled share message text.
 * @property {string[]|null} [heroImages] - Hero images shown in the initial modal screen.
 * @property {string} [logoUrl] - HTTPS URL of the overlay logo (PNG/WebP).
 * @property {string} [logoPosition] - Logo corner position.
 * @property {number} [logoSizePercent] - Logo width as percentage of image width.
 * @property {number} [logoMarginPercent] - Logo margin from image edge.
 */

/**
 * Builds the payload passed to `bnt_r_ai('render', 'virtual-try-on', ...)`.
 * Includes `overlayImages` only when a logo URL is configured.
 *
 * @param {BantoaVtoRawConfig} rawConfig - Config parsed from `data-bantoa-vto-config`.
 * @returns {Object} Bantoa VTO render options.
 */
function buildBantoaVtoConfig(rawConfig) {
  const config = {
    items: rawConfig.items,
    position: rawConfig.position,
    buttonText: rawConfig.buttonText,
    shareText: rawConfig.shareText,
    onModalOpenCallback() {
      window.dataLayer?.push({ event: 'vto_modal_open' });
    },
    onImageActionCallback() {
      window.dataLayer?.push({ event: 'vto_image_action' });
    },
    onAddToCartCallback() {
      window.dataLayer?.push({ event: 'vto_add_to_cart' });
    },
  };

  if (Array.isArray(rawConfig.heroImages) && rawConfig.heroImages.length > 0) {
    config.heroImages = rawConfig.heroImages;
  }

  if (rawConfig.logoUrl) {
    config.overlayImages = {
      type: 'logo',
      image: rawConfig.logoUrl,
      position: rawConfig.logoPosition || 'bottom-right',
      sizePercent: rawConfig.logoSizePercent ?? 15,
      marginPercent: rawConfig.logoMarginPercent ?? 3,
    };
  }

  return config;
}

/**
 * Opens the Bantoa Virtual Try-On modal for the given configuration.
 *
 * @param {BantoaVtoRawConfig} rawConfig - Config parsed from `data-bantoa-vto-config`.
 */
function openBantoaVirtualTryOn(rawConfig) {
  if (typeof bnt_r_ai !== 'function') {
    console.warn('[Bantoa VTO] bnt_r_ai is not available. Ensure the Bantoa pixel is loaded.');
    return;
  }

  bnt_r_ai('render', 'virtual-try-on', buildBantoaVtoConfig(rawConfig));
}

/**
 * Binds click handlers to all `[data-bantoa-vto-trigger]` elements on the page.
 */
function initBantoaVirtualTryOn() {
  document.querySelectorAll('[data-bantoa-vto-trigger]').forEach((trigger) => {
    if (trigger.dataset.bantoaVtoBound === 'true') return;

    trigger.dataset.bantoaVtoBound = 'true';
    trigger.addEventListener('click', () => {
      const configJson = trigger.getAttribute('data-bantoa-vto-config');
      if (!configJson) return;

      try {
        openBantoaVirtualTryOn(JSON.parse(configJson));
      } catch (error) {
        console.warn('[Bantoa VTO] Invalid config JSON on trigger.', error);
      }
    });
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initBantoaVirtualTryOn);
} else {
  initBantoaVirtualTryOn();
}
